/**
 * Mg Theme — Video Testimonials
 * (c) 2026 NAMY SOLUTIONS LLC – All Rights Reserved.
 *
 * Play-button swaps the cover for an autoplay YouTube/Vimeo iframe (global
 * delegated handler — works for dynamically-injected sections).
 *
 * <mg-video-testimonials-layout> is a custom element: slider mode builds the
 * Splide structure in connectedCallback then delegates to
 * window.MgSlider.mount (assets/mg-slider-core.js). Being a real custom
 * element (not a one-shot DOMContentLoaded init) means the Theme Editor's
 * section re-render re-upgrades the element and re-mounts the slider — the
 * historical "can't slide in the editor" bug (init events already fired before
 * the section was injected) cannot recur. Mirrors assets/mg-reviews.js.
 */
(function () {
  'use strict';

  function handlePlayClick(e) {
    var playBtn = e.target.closest('.mg-video-testimonial__play');
    if (!playBtn) return;

    var wrapper = playBtn.closest('.mg-video-testimonial__video');
    if (!wrapper) return;

    var type = wrapper.dataset.videoType;
    var id = wrapper.dataset.videoId;
    var src = '';

    if (type === 'youtube') {
      src = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
    } else if (type === 'vimeo') {
      src = 'https://player.vimeo.com/video/' + id + '?autoplay=1';
    }
    if (!src) return;

    try {
      wrapper.innerHTML =
        '<iframe src="' + src + '"' +
        ' class="mg-video-testimonial__iframe"' +
        ' allow="autoplay; encrypted-media" allowfullscreen loading="lazy"' +
        ' title="Video testimonial"></iframe>';
    } catch (err) {
      if (window.Mg && window.Mg.debug) Mg.debug('MgVideoTestimonials', err);
    }
  }

  if (!window.__namyVTClickBound) {
    window.__namyVTClickBound = true;
    document.addEventListener('click', handlePlayClick);
  }
})();

if (!customElements.get('mg-video-testimonials-layout')) {
  class MgVideoTestimonialsLayout extends HTMLElement {
    connectedCallback() {
      if (this.dataset.layout !== 'slider' || !window.MgSlider) return;
      if (this.splide) return;

      var list = this.querySelector('.mg-video-testimonials__list');
      if (!list) return;

      list.classList.add('splide__list');
      Array.from(list.children).forEach(function (slide) {
        if (slide.nodeType === 1 && slide.tagName.toLowerCase() !== 'link') {
          slide.classList.add('splide__slide');
        }
      });

      var splideRoot = window.MgSlider.wrapList(list);

      this.splide = window.MgSlider.mount(splideRoot, {
        arrows: this.dataset.arrows !== 'false',
        pagination: window.MgSlider.normalizePagination(this.dataset.pagination),
        labels: { prev: this.dataset.labelPrev, next: this.dataset.labelNext },
        config: {
          autoWidth: true,
          focus: 0,
          omitEnd: true,
          gap: '1.5rem',
          drag: true,
          snap: true,
          flickMaxPages: 1
        }
      });

      // autoWidth measures slide widths AT mount. When the host is injected
      // before layout settles (Theme Editor section re-render sets innerHTML,
      // upgrading this element synchronously before paint), the widths read 0
      // so Splide caches a one-page track and never translates — the slider
      // looks mounted (arrows/dots) but won't slide. Recompute once layout is
      // up: rAF for the fast path, plus a one-shot ResizeObserver that fires
      // the moment the root reports a real width (timing-independent, also
      // covers a section hidden at mount). Both are no-ops when sizes are
      // already correct (normal storefront load).
      if (this.splide) {
        var sp = this.splide;
        var refresh = function () { try { sp.refresh(); } catch (e) { void e; } };
        requestAnimationFrame(refresh);
        if ('ResizeObserver' in window) {
          var self = this;
          this._ro = new ResizeObserver(function (entries) {
            if (entries[0] && entries[0].contentRect.width > 0) {
              refresh();
              if (self._ro) { self._ro.disconnect(); self._ro = null; }
            }
          });
          this._ro.observe(splideRoot);
        }
      }
    }

    disconnectedCallback() {
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      if (this.splide) {
        /* Passe par le coeur : lui seul sait effacer la marque « deja monte »
           qu il a posee sur la racine. */
        if (window.MgSlider && MgSlider.unmount) MgSlider.unmount(this.splide);
        else try { this.splide.destroy(); } catch (e) { void e; }
        this.splide = null;
      }
    }
  }

  customElements.define('mg-video-testimonials-layout', MgVideoTestimonialsLayout);
}
